<template>
  <FlexLayout>
    <vue-loading :active="isLoading" :is-full-page="true" />

    <BlockContainer :title="$t('account.subTitle.personalInfo')" role="region">
      <div v-if="config.ACCOUNT.showWondCustomerDataOnly" v-html="formatCustomerAddress(wondCustomer)"></div>
      <div v-else-if="primaryCustomerAddress" v-html="primaryCustomerAddress.formattedAddressWithoutCData"></div>
      <template #actions>
        <Button v-if="config.ACCOUNT.myData.editable" variant="primary" @click="navigateToEditCustomer">{{ $t('account.button.editCustomer')}}</Button>
        <Button v-if="config.ADDRESS.enabled" variant="primary" @click="navigateToAddressBook">{{ $t('account.button.addressBook')}}</Button>
        <Button v-if="config.ACCOUNT.myData.changePassword" variant="primary" @click="navigateToPasswordReset">{{ $t('account.button.changePassword')}}</Button>
      </template>
    </BlockContainer>

    <BlockContainer v-if="config.USE_ALLERGEN_FILTERS" :title="$t('account.subTitle.dietaryInfo')" role="region">
      <span>{{$t('account.label.dietaryInfo',[diataryInfoCount])}}</span>
      <template #actions>
        <Button variant="primary" @click="viewAllergenDietaryFilter" :aria-label="$t('account.ariaLabel.dietaryInfo')">{{ $t('account.button.dietaryInfo') }}</Button>
      </template>
    </BlockContainer>

    <!-- Modal for allergen and dietary filters -->
    <b-modal id="allergen-dietary-filter-modal" hide-footer :title="$t('account.subTitle.dietaryInfo')" title-tag="h3" title-class="normalText">
      <AllergenDietaryFilter @edit-customer-dietary-attributes="handleEditCustomerDietaryAttributes" :selected-allergens="selectedAllergens" :selected-properties="selectedProperties"/>
    </b-modal>
  </FlexLayout>
</template>

<script>
import Utils from "@/utils/utils";
import {accountService} from "@/services/api/accountService";
import VueLoading from "vue-loading-overlay";
import AllergenDietaryFilter from "@/components/sub-components/AllergenDietaryFilter.vue";
import {customerService} from "@/services/api/customerService";
import {useCustomerStore} from "@/stores/customer";
import {useAppStore} from "@/stores/app";
import BlockContainer from "@/components/layout/BlockContainer.vue";
import FlexLayout from "@/components/layout-components/Main/FlexLayout.vue";
import Button from "@/components/actions/Button.vue";
import { formatCustomerAddress } from '@/utils/customerAddressUtils';

export default {
  data(){
    return {
      isLoading: false,
      customer: {},
      wondCustomer: {},
      selectedAllergens: [],
      selectedProperties: [],
    }
  },
  computed:{
    Utils() {
      return Utils
    },
    config(){
      return window.config;
    },
    diataryInfoCount() {
      return this.selectedAllergens.length + this.selectedProperties.length;
    },
    primaryCustomerAddress() {
      if (!this.customer.addresses || !this.customer.addresses.length) return null;
      return this.customer.addresses.find(addr => addr.isPrimary) || this.customer.addresses[0];
    },
  },
  components:{
    Button,
    FlexLayout,
    BlockContainer,
    AllergenDietaryFilter,
    VueLoading,
  },
  setup() {
    const customerStore = useCustomerStore();
    const appStore = useAppStore();

    return {
      customerStore,
      appStore,
    };
  },
  methods:{
    formatCustomerAddress,
    async handleEditCustomerDietaryAttributes(attributes) {
      this.loading = true;
      const result = await customerService.editCustomerDietaryAttributes(attributes);
      this.loading = false;

      this.customerStore.setDietaryInfo(result.wondCustomer.dietaryInfo);
      this.selectedAllergens = result.wondCustomer.allergenIds || [];
      this.selectedProperties = result.wondCustomer.propertyIds || [];

      this.$bvModal.hide("allergen-dietary-filter-modal");
      this.makeToast('success', this.$t('account.messages.info.dietaryInfoUpdated'));
    },
    navigateToEditCustomer() {
      this.$navigateToEditCustomer();
    },
    navigateToAddressBook() {
      this.$navigateToAddressBook();
    },
    navigateToPasswordReset() {
      this.$navigateToPasswordReset();
    },
    viewAllergenDietaryFilter() {
      this.$bvModal.show("allergen-dietary-filter-modal");
    },
    async logout() {
      const result = await accountService.logout();
      if (result.error) {
        this.makeToast('danger', result.message);
      } else {
        this.makeToast('success', this.$t('account.messages.info.logout'));
        this.$navigateToLogin();
      }
    },
  },
  async created() {
    const flashToast = this.appStore.consumeFlashToast();

    const config = this.config;

    this.isLoading = true;
    const result = await accountService.afterLogin();
    this.isLoading = false;

    if (!result || result.exception !== undefined) {
      this.makeToast('danger', this.$t("account.messages.error.loadCustomerData"));
      return;
    }

    if (config.USE_ALLERGEN_FILTERS && result.wondCustomer && result.wondCustomer.allergenIds !== undefined) {
      this.selectedAllergens = result.wondCustomer.allergenIds;
    }

    if (config.USE_ALLERGEN_FILTERS && result.wondCustomer && result.wondCustomer.propertyIds !== undefined) {
      this.selectedProperties = result.wondCustomer.propertyIds;
    }

    this.customer = result.customer;
    this.wondCustomer = result.wondCustomer;

    if (flashToast?.variant && flashToast?.message) {
      this.makeToast(flashToast.variant, flashToast.message);
    }
  },
}
</script>

<style scoped>
.accountPageFlexer > * {
  flex: 1 1 350px;
  max-width: 100%;
}
.address-header {
  display: flex;
  align-items: center;
}
.primary-indicator {
  color: gold;
  position: absolute;
  top: 0.2em;
  right: 0.5em;
  font-size: 1.3em;
  z-index: 1;
}
.formatted-name {
  font-weight: bold;
}
.address-flex-row {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
}
.phonebook-image {
  width: 64px;
  height: 64px;
  object-fit: contain;
  margin-top: 0.5rem;
}
.address-datagrid-wrapper {
  flex: 1;
}
.address-cell {
  position: relative;
  padding-top: 1.2em;
}
</style>
